// LB-315 — the system back gesture (iOS edge swipe, Android back, the browser button) on every
// surface. One module, three ideas, ONE popstate listener:
//
//   1. A ROOT entry. The app lays it on the CURRENT history entry when it mounts (replaceState,
//      never a push) and again on every mode switch (a new "generation"), first walking back
//      over the old mode's entries so nothing of that mode sits beneath the new root. At the
//      root, back is the platform's own behaviour (Alan, 30 Sep, decision B): with nothing
//      beneath — the installed app — an iPhone swipe is inert and Android back leaves the app.
//      The first cut kept a "floor" entry beneath the root and re-pushed the root on landing;
//      Safari drew its stale snapshot of the floor for seconds on every swipe, so it went.
//      An entry that is still foreign (pre-app in the same document, a stale one from before
//      a reload, an older generation) re-pushes the root — that only arises in a session opened
//      from the landing page.
//   2. TAB entries (decision A, 29 Sep): each tab change in either mode pushes an entry, so
//      back steps Kids → Home; the unsaved-changes guard runs on the pop as it does on a tap.
//   3. LAYERS: anything that opens over a screen calls useBackLayer(open, onClose). Opening
//      pushes one entry; the gesture pops the TOPMOST layer only; closing by X / Done consumes
//      the entry (history.back with the pop swallowed), so no orphan is left for the next swipe
//      to land on and appear to do nothing. A layer may carry a guard: asked on the pop, it can
//      veto (the entry is re-pushed) and take over — the unsaved-changes dialog, "Leave the
//      test?" mid-game (TestOverlay).
//
// Every entry the app writes carries `lbGen`. Entries without it, or with an older one, are
// foreign and trigger the root re-push. Nothing here reads or writes household state, and the
// URL is never changed (pushState with "" keeps it), so deep links and the ?preview harness are
// untouched.
// React is a vendor global (CLAUDE.md: no react imports under src/).
const { useEffect, useRef } = React;

const H = () => (typeof window !== "undefined" ? window.history : null);

let gen = 0;            // current generation; bumped on every root reset
let seq = 0;            // layer ids
const layers = [];      // open layers, bottom → top: { id, onClose, guard, entry }
const dead = new Set(); // ids of layers closed while NOT on top: their entries are skipped on landing
let ownPops = 0;        // pops caused by our own history.go/back, not the user's gesture
// How many entries our own pops still have to travel. A browser's history.go() lands on a later
// tick, so until it does, state() still reads the entry we are LEAVING. Anything that measures
// distance from state() in the meantime (a mode switch walking back to the root) must subtract
// this, or two moves issued in one tick overshoot the app's first entry and the browser leaves
// the page (nightly Playwright, 1 Oct: entering Parent Mode closes the PIN pad in the same tick;
// Kid Mode can be tapped with a Settings sheet open).
let inFlight = 0;
// Our own history move of `n` entries back. False when the browser refused it.
function ownGo(n) {
  ownPops += 1; inFlight += n;
  try { H().go(-n); return true; } catch { ownPops -= 1; inFlight -= n; return false; }
}
const order = [];       // every layer id ever pushed, in history order (dead ones included until consumed)
let tabs = {};          // mode → { get, set, guard }
let getMode = () => null;
let installed = false;

function state() { try { return (H() && H().state) || {}; } catch { return {}; } }
function push(entry) {
  // A pushState discards every history entry above the current one, so any layer whose entry
  // sat above us is gone with it: forget those ids or a later close would count them.
  const cur = state().lbLayer;
  const keep = cur != null ? order.indexOf(cur) + 1 : 0;
  for (const id of order.slice(keep)) { dead.delete(id); }
  order.splice(keep);
  try { H().pushState({ ...entry, lbIdx: (Number(state().lbIdx) || 0) + 1 }, ""); } catch {}
}
function replace(entry) { try { H().replaceState(entry, ""); } catch {} }

// The root entry for the CURRENT mode/tab. Layers spread the live state so a popped layer
// lands on the entry beneath with its tab intact.
function rootEntry(extra) { return { lbGen: gen, lbRoot: true, lbIdx: 0, ...(extra || {}) }; }
function isCurrent(st) { return !!st && st.lbGen === gen; }

// Called from App on mount and on every mode switch. A new generation: everything before is
// foreign. Every entry carries `lbIdx`, its height above the root, so a switch knows how far
// back the old root is: it walks there (one history.go, the pop is ours) and replaces it.
let pendingRoot = null;
export function resetRoot(mode, tab) {
  gen += 1;
  layers.length = 0; dead.clear(); order.length = 0;
  const entry = rootEntry(tab ? { lbTab: tab, lbMode: mode } : { lbMode: mode });
  // Distance to the old root from where our own pops in flight will land, not from the entry
  // state() still shows.
  const idx = (Number(state().lbIdx) || 0) - inFlight;
  if (idx > 0) { pendingRoot = entry; if (!ownGo(idx)) { pendingRoot = null; replace(entry); } }
  else if (ownPops > 0) pendingRoot = entry; // a pop already landing there: lay the root when it does
  else replace(entry);
}

export function currentGen() { return gen; }
export function openLayers() { return layers.map((l) => l.id); }

function onPop() {
  const st = state();
  // 1. Our own history.go() from closeLayer: nothing to close. If it left us on the entry
  //    of a layer that was closed while not on top (a browser's back() is asynchronous, so
  //    two layers closing together can land here — CC, 29 Sep), keep going past it.
  if (ownPops > 0) {
    ownPops -= 1;
    if (ownPops === 0) inFlight = 0;
    // A mode switch walking back to the old root: lay the new root on it and stop — once the
    // LAST of our pops has landed (a layer's close may have been in flight with it).
    if (pendingRoot) { if (ownPops === 0) { const e = pendingRoot; pendingRoot = null; replace(e); } return; }
    if (st.lbLayer != null && dead.has(st.lbLayer)) { dead.delete(st.lbLayer); dropOrder(st.lbLayer); ownGo(1); return; }
    // A layer closed by its own control while the tab had moved under it (a sheet open on
    // Kids, then a tap on Home): the entry we land on names the old tab. Correct it in place
    // rather than let the next swipe appear to do nothing.
    const t = tabs[getMode()];
    if (t && isCurrent(st) && st.lbTab && st.lbTab !== t.get()) replace({ ...st, lbTab: t.get() });
    return;
  }
  // 2. Foreign entry (pre-app in the same document, stale, or an older mode's): stand still.
  if (!isCurrent(st)) { push(rootEntry({ lbTab: tabs[getMode()] ? tabs[getMode()].get() : undefined, lbMode: getMode() })); return; }
  // 3. Layers above the entry we landed on close, topmost first, one per pop.
  const top = layers[layers.length - 1];
  if (top && st.lbLayer !== top.id) {
    if (top.guard && top.guard() === false) { push(top.entry); return; } // vetoed: the layer took over
    layers.pop(); dropOrder(top.id);
    try { top.onClose(); } catch {}
    return;
  }
  // 4. Landed on the entry of a layer that was closed while not on top: skip it.
  if (st.lbLayer != null && dead.has(st.lbLayer)) { dead.delete(st.lbLayer); dropOrder(st.lbLayer); ownGo(1); return; }
  // 5. A tab entry of the current mode.
  const t = tabs[getMode()];
  if (t && st.lbTab && st.lbTab !== t.get()) {
    if (t.guard && t.guard() === false) { push({ ...state(), lbTab: t.get() }); return; }
    t.set(st.lbTab, true);
  }
}

export function installBackStack(modeGetter) {
  getMode = modeGetter;
  if (installed || typeof window === "undefined") return () => {};
  installed = true;
  window.addEventListener("popstate", onPop);
  return () => { window.removeEventListener("popstate", onPop); installed = false; };
}

// ---- tabs ---------------------------------------------------------------------------------
// Register the tab of a mode. `set(tab, fromPop)` is the mode's setter; when fromPop is true the
// caller must NOT push (the entry is already there). Returns an unregister.
export function registerTabs(mode, api) { tabs[mode] = api; return () => { if (tabs[mode] === api) delete tabs[mode]; }; }
// The mode's tab changed by a tap: one entry. Skipped while a layer is open (a tab change
// under an open sheet is not a navigation the gesture should retrace).
export function tabChanged(tab) {
  const st = state();
  if (layers.length) return;
  if (st.lbTab === tab && isCurrent(st)) return;
  if (!isCurrent(st)) { replace(rootEntry({ lbTab: tab, lbMode: getMode() })); return; }
  push({ ...st, lbTab: tab, lbRoot: undefined, lbLayer: undefined });
}

// ---- layers -------------------------------------------------------------------------------
export function pushLayer(onClose, guard) {
  const id = ++seq;
  const entry = { ...state(), lbGen: gen, lbLayer: id, lbRoot: undefined };
  layers.push({ id, onClose, guard, entry });
  order.push(id);
  push(entry);
  return id;
}
export function updateLayer(id, onClose, guard) {
  const l = layers.find((x) => x.id === id);
  if (l) { l.onClose = onClose; l.guard = guard; }
}
// Closed by X / Done / programmatically: consume the entry so the next swipe has nothing stale.
export function closeLayer(id) {
  const i = layers.findIndex((x) => x.id === id);
  if (i < 0) return;
  const wasTop = i === layers.length - 1;
  layers.splice(i, 1);
  if (wasTop && state().lbLayer === id) {
    // Consume this entry AND any dead entries directly beneath it in one go, so the app
    // never sits on the entry of a layer that is already closed (CC, 29 Sep: from there
    // every tab entry was recorded on top of a dead one and a later back skipped it).
    let n = 1; let k = order.lastIndexOf(id) - 1;
    while (k >= 0 && dead.has(order[k])) { dead.delete(order[k]); n += 1; k -= 1; }
    order.splice(k + 1);
    ownGo(n);
  } else dead.add(id);
}

// The hook every overlay uses. `open` true → one entry; false → the entry is consumed if the
// layer was still open (closed by its own control). `onClose` is what the gesture calls;
// `guard` (optional) returns false to veto the pop and handle it itself.
export function useBackLayer(open, onClose, guard) {
  const idRef = useRef(null);
  const closeRef = useRef(onClose); closeRef.current = onClose;
  const guardRef = useRef(guard); guardRef.current = guard;
  useEffect(() => {
    if (!open) return undefined;
    const id = pushLayer(() => { idRef.current = null; closeRef.current && closeRef.current(); }, () => (guardRef.current ? guardRef.current() : undefined));
    idRef.current = id;
    return () => { if (idRef.current === id) { idRef.current = null; closeLayer(id); } };
  }, [open]);
}

// A stack of pages inside one component (the Learn & Earn hub's frames): one layer per page
// above the first. `depth` = pages above the first; `onBack` pops one page. A page closed by
// its own Back consumes its entry; a page opened pushes one.
export function useBackDepth(depth, onBack) {
  const idsRef = useRef([]);
  const backRef = useRef(onBack); backRef.current = onBack;
  useEffect(() => {
    const ids = idsRef.current;
    while (ids.length < depth) ids.push(pushLayer(() => { idsRef.current.pop(); backRef.current && backRef.current(); }));
    while (ids.length > depth) closeLayer(ids.pop());
  }, [depth]);
  useEffect(() => () => { const ids = idsRef.current; while (ids.length) closeLayer(ids.pop()); }, []);
}

// For the harness only.
function dropOrder(id) { const k = order.indexOf(id); if (k >= 0) order.splice(k, 1); }
export function _reset() { gen = 0; seq = 0; layers.length = 0; dead.clear(); order.length = 0; ownPops = 0; inFlight = 0; pendingRoot = null; tabs = {}; }
